/**
 * Fase 3 · Recorder overlay per Feedback Hub audio exercise.
 *
 * The overlay is positioned by FH-provided coordinates (`x, y, width,
 * height` in PDF points, top-left origin) and scaled by the viewer.
 * ActiveEnglish NEVER detects microphones — the coordinates always come
 * from `review.audio_exercises[]` or from the response of
 * `POST /submit-activity` (Fase 3 authorized flow).
 *
 * Three simplified operating modes (per user spec 2026-08-14):
 *   • `student-record` — before the student finalises the activity: they
 *     can record → play → re-record freely. Each POST goes to the
 *     ActiveEnglish proxy `/activity/{id}/student-audio`, which forwards
 *     to Feedback Hub `/student-audio`. FH keeps every attempt in its
 *     `history[]`; the UI shows the latest.
 *   • `teacher-record` — teacher reviewing an activity in the teacher's
 *     PDF viewer: they can play the student's audio and optionally
 *     record a pronunciation correction of their own. Same re-record
 *     freedom as the student, until they publish the review.
 *   • `student-view` — student in «Correcciones del tutor»: playback
 *     only. Never re-records. Existing Fase 2 «Corrección enviada»
 *     lock stays intact — this component does not touch it.
 *
 * MediaRecorder MIME picked in order: audio/webm;codecs=opus → audio/webm
 * → audio/mp4 (Safari) → audio/ogg;codecs=opus. All are on the AE
 * backend allow-list.
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Mic, Square, Play, Pause, RotateCcw, Send, X } from 'lucide-react';

const PREFERRED_MIMES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
];

function pickSupportedMime() {
  if (typeof MediaRecorder === 'undefined') return null;
  for (const m of PREFERRED_MIMES) {
    try {
      if (MediaRecorder.isTypeSupported(m)) return m;
    } catch {
      // continue
    }
  }
  return null;
}

// Small helper: format seconds like "0:07"
function fmtDuration(sec) {
  const s = Math.max(0, Math.floor(sec || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

// Feedback Hub returns audio pointers in two possible shapes depending on
// version: (a) an object like `{audio_id, attempt, ...}` (contract §10) or
// (b) a URL string `/api/feedback/audio/{audio_id}/file`. History entries
// carry `audio_url` instead of `audio_id`. Normalise everything to the
// bare `audio_id` so the rest of the component can stay simple.
function resolveAudioId(pointer) {
  if (!pointer) return null;
  if (typeof pointer === 'string') {
    const m = pointer.match(/\/audio\/([^/?#]+)\/file/);
    return m ? m[1] : null;
  }
  if (typeof pointer === 'object') {
    if (pointer.audio_id) return pointer.audio_id;
    if (pointer.id) return pointer.id;
    if (pointer.audio_url) return resolveAudioId(pointer.audio_url);
  }
  return null;
}

/**
 * Props:
 *   exercise:  { exercise_id, page, x, y, width, height, student_audio?, teacher_audio?,
 *                _preSubmitLocal?: { blobUrl, mime, filename, durationSec } }
 *   scale:     number (multiply by PDF points to get pixels)
 *   mode:      'student-record' | 'teacher-record' | 'student-view' | 'student-pre-submit'
 *   activityId: string | null (null in `student-pre-submit` — blobs stay local)
 *   API:       axios base URL
 *   token:     JWT (only used for playback of persisted audios; local blobs
 *              use object URLs)
 *   onUploaded(fhResponse) — called after a successful upload of a
 *              persisted (post-submit) audio.
 *   onLocalBlobChange(exercise_id, blobInfo|null) — called in
 *              `student-pre-submit` mode with `{ blob, mime, filename }`
 *              when the student saves a local recording, and with `null`
 *              when they discard it.
 */
export function AudioExerciseOverlay({
  exercise, scale, mode, activityId, API, token, onUploaded, onLocalBlobChange,
  // Fase 5.1 iter69 · Sidebar recorder pattern (mirrors the audio player).
  // The parent keeps a single-selection `openRecorderId` and provides a
  // DOM node (`sidebarHost`) mounted at the viewer root where the panel
  // gets portaled while scrolling the PDF. When `isOpen` is undefined
  // (older callers) the panel falls back to the previous in-page
  // rendering so nothing breaks in isolation.
  isOpen: isOpenExternal, onOpenChange, sidebarHost,
}) {
  const { exercise_id, x, y, width, height } = exercise;
  const left = (Number(x) || 0) * scale;
  const top = (Number(y) || 0) * scale;
  const w = Math.max(24, (Number(width) || 0) * scale);
  const h = Math.max(24, (Number(height) || 0) * scale);

  // The audio proxy needs the activity_id to enforce ownership. Build a
  // reusable prefix for <audio src> URLs.
  const audioSrc = (aid) =>
    `${API}/activities/audio/${aid}/file?token=${encodeURIComponent(token)}&activity_id=${encodeURIComponent(activityId || '')}`;

  // Persisted (already-uploaded) audios from FH. FH's `student_audio` /
  // `teacher_audio` may arrive as either an object (`{audio_id, ...}`) or
  // as a raw URL string; `resolveAudioId` normalises both.
  const studentAudioId = resolveAudioId(exercise.student_audio);
  const teacherAudioId = resolveAudioId(exercise.teacher_audio);

  const canRecord = mode === 'student-record' || mode === 'teacher-record' || mode === 'student-pre-submit';
  const isPreSubmit = mode === 'student-pre-submit';
  const uploadPath = mode === 'teacher-record' ? 'teacher-audio' : 'student-audio';
  // What the CURRENT actor is uploading — the counterpart audio is always
  // visible for reference (student sees teacher's; teacher sees student's).
  const myPersistedAudioId = mode === 'teacher-record' ? teacherAudioId : studentAudioId;
  const otherPersistedAudioId = mode === 'teacher-record' ? studentAudioId : teacherAudioId;
  const otherLabel = mode === 'teacher-record' ? 'Audio del alumno' : 'Audio del docente';
  const myLabel = mode === 'teacher-record' ? 'Tu corrección' : 'Tu grabación';

  // Local recording state
  const [status, setStatus] = useState('idle'); // idle | permission | recording | ready | uploading | uploaded | error
  const [errorMsg, setErrorMsg] = useState('');
  const [duration, setDuration] = useState(0);
  const [localBlobUrl, setLocalBlobUrl] = useState(null);
  const [localBlob, setLocalBlob] = useState(null);
  // Auto-expand for the student in «Correcciones del tutor» so they never
  // miss the teacher's audio. For every other mode start collapsed so
  // the panel doesn't cover the exercise sentence — the record button
  // and the "+" toggle stay visible even when collapsed.
  const _hasAnyPersisted = !!(
    resolveAudioId(exercise.student_audio) || resolveAudioId(exercise.teacher_audio)
  );
  const [expanded, setExpanded] = useState(
    mode === 'student-view' && _hasAnyPersisted,
  );
  const [showHistory, setShowHistory] = useState(false);

  // Fase 3 iter59 · derive visibility from data. When a persisted
  // audio_id appears mid-life (e.g. after a pre-submit blob upload
  // completes and the parent flips `audioMode` from 'student-pre-submit'
  // to 'student-record', which remounts the component and resets the
  // initial state), auto-expand so the persisted player mounts without
  // requiring the user to click the toggle.
  useEffect(() => {
    if (studentAudioId || teacherAudioId) setExpanded(true);
  }, [studentAudioId, teacherAudioId]);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const startTsRef = useRef(0);
  const timerRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    if (localBlobUrl) URL.revokeObjectURL(localBlobUrl);
  }, []);

  const startRecording = useCallback(async () => {
    if (!canRecord) return;
    setErrorMsg('');
    setStatus('permission');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickSupportedMime();
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const type = mr.mimeType || mime || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type });
        setLocalBlob(blob);
        const url = URL.createObjectURL(blob);
        setLocalBlobUrl(prev => { if (prev) URL.revokeObjectURL(prev); return url; });
        setStatus('ready');
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(t => t.stop());
          streamRef.current = null;
        }
      };
      mr.start();
      startTsRef.current = Date.now();
      setDuration(0);
      timerRef.current = setInterval(() => {
        setDuration((Date.now() - startTsRef.current) / 1000);
      }, 250);
      setStatus('recording');
      setExpanded(true);
    } catch (e) {
      const msg = e?.name === 'NotAllowedError'
        ? 'Permiso de micrófono denegado'
        : (e?.message || 'No se pudo iniciar la grabación');
      setErrorMsg(msg);
      setStatus('error');
      toast.error(msg);
    }
  }, [canRecord]);

  const stopRecording = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    const mr = mediaRecorderRef.current;
    if (mr && mr.state === 'recording') mr.stop();
  }, []);

  const discardLocal = useCallback(() => {
    if (localBlobUrl) URL.revokeObjectURL(localBlobUrl);
    setLocalBlob(null);
    setLocalBlobUrl(null);
    setDuration(0);
    setStatus('idle');
  }, [localBlobUrl]);

  const uploadLocal = useCallback(async () => {
    if (!localBlob) return;
    // Fase 3 · pre-submit branch: no activity_id yet. Hand the blob off
    // to the parent so it can upload later, at submit time.
    if (isPreSubmit) {
      const type = localBlob.type || 'audio/webm';
      const ext = type.includes('mp4') ? 'm4a'
        : type.includes('ogg') ? 'ogg'
        : type.includes('wav') ? 'wav'
        : 'webm';
      onLocalBlobChange?.(exercise_id, {
        blob: localBlob,
        mime: type,
        filename: `${exercise_id}.${ext}`,
      });
      setStatus('uploaded');
      toast.success('Audio guardado (se enviará al pulsar «Entregar»)');
      // Keep the local blob around briefly so the "player" area doesn't
      // flash empty; parent will re-inject via `exercise._preSubmitLocal`.
      if (localBlobUrl) URL.revokeObjectURL(localBlobUrl);
      setLocalBlob(null);
      setLocalBlobUrl(null);
      setDuration(0);
      return;
    }
    if (!activityId) return;
    setStatus('uploading');
    try {
      const fd = new FormData();
      // Derive a filename with the appropriate extension.
      const type = localBlob.type || 'audio/webm';
      const ext = type.includes('mp4') ? 'm4a'
        : type.includes('ogg') ? 'ogg'
        : type.includes('wav') ? 'wav'
        : 'webm';
      fd.append('file', new File([localBlob], `${exercise_id}.${ext}`, { type }));
      fd.append('exercise_id', exercise_id);
      const res = await axios.post(
        `${API}/activities/activity/${activityId}/${uploadPath}`,
        fd,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setStatus('uploaded');
      toast.success(mode === 'teacher-record' ? 'Corrección enviada al ejercicio' : 'Audio guardado');
      // Clear the local blob — parent will refresh and the persisted
      // audio will be shown via `myPersistedAudioId`.
      if (localBlobUrl) URL.revokeObjectURL(localBlobUrl);
      setLocalBlob(null);
      setLocalBlobUrl(null);
      setDuration(0);
      onUploaded?.(res.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'No se pudo guardar el audio';
      setErrorMsg(msg);
      setStatus('error');
      toast.error(msg);
    }
  }, [localBlob, activityId, exercise_id, uploadPath, mode, API, token, localBlobUrl, onUploaded, isPreSubmit, onLocalBlobChange]);

  const discardPreSubmit = useCallback(() => {
    // Fase 3 · pre-submit discard: also drops the blob from the parent's map.
    onLocalBlobChange?.(exercise_id, null);
    setStatus('idle');
  }, [exercise_id, onLocalBlobChange]);

  // Compact when there's nothing actively happening — the panel becomes a
  // small pill next to the mic icon, so it doesn't cover the sentence the
  // student must read aloud. Expands automatically while recording / ready
  // / uploading, or when the user clicks the pill.
  const activeRecording = status === 'recording' || status === 'ready' || status === 'uploading';
  // iter69 · when the parent controls the open/closed state (sidebar
  // mode), reuse that; otherwise fall back to the local `expanded` flag
  // so the legacy in-page rendering keeps working outside of a viewer.
  const externallyControlled = typeof isOpenExternal === 'boolean' && typeof onOpenChange === 'function';
  const isOpen = externallyControlled ? isOpenExternal : expanded;
  const toggleOpen = () => {
    if (externallyControlled) onOpenChange(!isOpen);
    else setExpanded(v => !v);
  };
  const closePanel = () => {
    if (externallyControlled) onOpenChange(false);
    else setExpanded(false);
  };
  const showPanel = isOpen || activeRecording;
  const useSidebar = externallyControlled && !!sidebarHost;
  const overlayHasAnyPersisted = !!(myPersistedAudioId || otherPersistedAudioId);
  const isBusy = status === 'recording' || status === 'uploading' || status === 'permission';

  // iter69 · Extract the full panel JSX so we can render it either in
  // the legacy in-page position OR inside the viewer's right-hand sidebar
  // via a portal. The panel contents / behaviour are IDENTICAL — only
  // the position and stacking context change.
  const panelBody = (
    <div
      data-testid={`audio-exercise-panel-${exercise_id}`}
      style={useSidebar ? {
        // Sidebar mode: fill the host, no absolute positioning. Host
        // provides the right-side placement + top offset + z-index.
        background: '#ffffff',
        border: '1px solid #c7d2fe',
        borderRadius: 6,
        padding: 8,
        boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
        fontSize: 12,
        width: 280,
      } : {
        position: 'absolute',
        right: 4,
        top,
        minWidth: showPanel ? 220 : 96,
        maxWidth: 320,
        zIndex: 12,
        background: '#ffffff',
        border: '1px solid #c7d2fe',
        borderRadius: 6,
        padding: showPanel ? 8 : 3,
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        fontSize: 12,
      }}
    >
      <div
        data-testid={`audio-exercise-header-${exercise_id}`}
        role={activeRecording ? undefined : 'button'}
        tabIndex={activeRecording ? -1 : 0}
        onClick={activeRecording ? undefined : toggleOpen}
        onKeyDown={activeRecording ? undefined : (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleOpen();
          }
        }}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6,
          cursor: activeRecording ? 'default' : 'pointer',
          userSelect: 'none',
        }}
      >
        <div style={{ fontWeight: 600, color: '#4338ca', display: 'flex', alignItems: 'center', gap: 4 }}>
          <Mic size={13} /> {exercise_id}
          {!showPanel && (overlayHasAnyPersisted || (isPreSubmit && exercise._preSubmitLocal)) && (
            <span
              data-testid={`audio-persisted-badge-${exercise_id}`}
              style={{
                fontSize: 9, background: '#eef2ff', color: '#4338ca',
                padding: '1px 5px', borderRadius: 8,
              }}
              title="Hay audios grabados — clic para expandir"
            >
              ▶ {(myPersistedAudioId ? 1 : 0) + (otherPersistedAudioId ? 1 : 0) + (isPreSubmit && exercise._preSubmitLocal ? 1 : 0)}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {canRecord && status !== 'recording' && (
            <button
              type="button"
              data-testid={`audio-record-btn-${exercise_id}`}
              onClick={(e) => { e.stopPropagation(); startRecording(); }}
              disabled={isBusy}
              style={{
                background: '#dc2626', color: '#fff', border: 'none',
                padding: '3px 8px', borderRadius: 4, fontSize: 11,
                cursor: isBusy ? 'default' : 'pointer', fontWeight: 600,
                opacity: isBusy ? 0.5 : 1,
                display: 'flex', alignItems: 'center', gap: 3,
              }}
            >
              <Mic size={11} /> {(myPersistedAudioId || localBlob || (isPreSubmit && exercise._preSubmitLocal)) ? 'Regrabar' : 'Grabar'}
            </button>
          )}
          {status === 'recording' && (
            <button
              type="button"
              data-testid={`audio-stop-btn-${exercise_id}`}
              onClick={(e) => { e.stopPropagation(); stopRecording(); }}
              style={{
                background: '#111827', color: '#fff', border: 'none',
                padding: '3px 8px', borderRadius: 4, fontSize: 11,
                cursor: 'pointer', fontWeight: 600,
                display: 'flex', alignItems: 'center', gap: 3,
              }}
            >
              <Square size={11} /> Detener {fmtDuration(duration)}
            </button>
          )}
          {useSidebar && !activeRecording && (
            <button
              type="button"
              data-testid={`audio-exercise-close-${exercise_id}`}
              onClick={(e) => { e.stopPropagation(); closePanel(); }}
              aria-label="Cerrar grabador"
              title="Cerrar"
              style={{
                background: 'transparent', color: '#4338ca',
                border: '1px solid #c7d2fe',
                borderRadius: 4, padding: '0 5px',
                fontSize: 10, cursor: 'pointer', lineHeight: 1.4,
              }}
            >
              <X size={11} />
            </button>
          )}
        </div>
      </div>
      {status === 'recording' && (
        <div style={{ marginTop: 6, color: '#dc2626', fontWeight: 500 }}>
          ● Grabando… {fmtDuration(duration)}
        </div>
      )}
      {status === 'ready' && localBlobUrl && (
        <div style={{ marginTop: 6 }}>
          <div style={{ fontSize: 11, opacity: 0.75, marginBottom: 3 }}>{myLabel} (no guardado aún)</div>
          <audio
            data-testid={`audio-local-player-${exercise_id}`}
            src={localBlobUrl}
            controls
            style={{ width: '100%', height: 32 }}
          />
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            <button
              type="button"
              data-testid={`audio-upload-btn-${exercise_id}`}
              onClick={(e) => { e.stopPropagation(); uploadLocal(); }}
              disabled={status === 'uploading'}
              style={{
                background: '#059669', color: '#fff', border: 'none',
                padding: '4px 8px', borderRadius: 4, fontSize: 11,
                cursor: 'pointer', fontWeight: 600,
                display: 'inline-flex', alignItems: 'center', gap: 3,
              }}
            >
              <Send size={11} /> Guardar
            </button>
            <button
              type="button"
              data-testid={`audio-discard-btn-${exercise_id}`}
              onClick={(e) => { e.stopPropagation(); discardLocal(); }}
              style={{
                background: 'transparent', color: '#374151',
                border: '1px solid #d1d5db',
                padding: '4px 8px', borderRadius: 4, fontSize: 11,
                cursor: 'pointer', fontWeight: 500,
                display: 'inline-flex', alignItems: 'center', gap: 3,
              }}
            >
              <RotateCcw size={11} /> Descartar
            </button>
          </div>
        </div>
      )}
      {status === 'uploading' && (
        <div style={{ marginTop: 6, opacity: 0.8 }}>Guardando audio…</div>
      )}
      {status === 'error' && errorMsg && (
        <div style={{ marginTop: 6, color: '#dc2626' }}>{errorMsg}</div>
      )}
      {myPersistedAudioId && status !== 'ready' && status !== 'recording' && showPanel && !isPreSubmit && (
        <div style={{ marginTop: 6 }}>
          <div style={{ fontSize: 11, opacity: 0.75, marginBottom: 3 }}>{myLabel}</div>
          <audio
            data-testid={`audio-my-player-${exercise_id}`}
            src={audioSrc(myPersistedAudioId)}
            controls
            preload="metadata"
            style={{ width: '100%', height: 32 }}
          />
        </div>
      )}
      {otherPersistedAudioId && showPanel && !isPreSubmit && (
        <div style={{ marginTop: 6 }}>
          <div style={{ fontSize: 11, opacity: 0.75, marginBottom: 3 }}>{otherLabel}</div>
          <audio
            data-testid={`audio-other-player-${exercise_id}`}
            src={audioSrc(otherPersistedAudioId)}
            controls
            preload="metadata"
            style={{ width: '100%', height: 32 }}
          />
        </div>
      )}
      {isPreSubmit && exercise._preSubmitLocal?.blobUrl && status !== 'ready' && status !== 'recording' && showPanel && (
        <div style={{ marginTop: 6 }}>
          <div style={{ fontSize: 11, opacity: 0.75, marginBottom: 3 }}>
            Tu grabación (pendiente de entrega)
          </div>
          <audio
            data-testid={`audio-presubmit-local-${exercise_id}`}
            src={exercise._preSubmitLocal.blobUrl}
            controls
            preload="metadata"
            style={{ width: '100%', height: 32 }}
          />
          <button
            type="button"
            data-testid={`audio-presubmit-discard-${exercise_id}`}
            onClick={(e) => { e.stopPropagation(); discardPreSubmit(); }}
            style={{
              marginTop: 4,
              background: 'transparent', color: '#374151',
              border: '1px solid #d1d5db',
              padding: '2px 6px', borderRadius: 4, fontSize: 10,
              cursor: 'pointer',
            }}
          >
            Eliminar grabación
          </button>
        </div>
      )}
      {mode === 'student-view' && !overlayHasAnyPersisted && showPanel && (
        <div style={{ marginTop: 4, opacity: 0.6 }}>Sin audios grabados</div>
      )}
      {showPanel && Array.isArray(exercise.history) && exercise.history.length > 1 && (
        <div style={{ marginTop: 6 }}>
          <button
            type="button"
            data-testid={`audio-history-toggle-${exercise_id}`}
            onClick={() => setShowHistory(v => !v)}
            style={{
              background: 'transparent', color: '#4338ca',
              border: 'none', padding: 0, fontSize: 11,
              cursor: 'pointer', textDecoration: 'underline',
            }}
          >
            {showHistory ? 'Ocultar historial' : `Historial (${exercise.history.length})`}
          </button>
          {showHistory && (
            <div style={{ marginTop: 4, maxHeight: 140, overflowY: 'auto' }}>
              {exercise.history.map((h, i) => {
                const hid = resolveAudioId(h);
                if (!hid) return null;
                return (
                  <div key={hid} style={{ marginBottom: 4 }}>
                    <div style={{ fontSize: 10, opacity: 0.7 }}>
                      {h.role || 'audio'} · intento {h.attempt ?? i + 1}
                    </div>
                    <audio
                      src={audioSrc(hid)}
                      controls
                      preload="none"
                      style={{ width: '100%', height: 28 }}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Halo box positioned exactly over the FH exercise rect. */}
      <div
        data-testid={`audio-exercise-${exercise_id}`}
        data-audio-exercise-id={exercise_id}
        data-audio-mode={mode}
        style={{
          position: 'absolute',
          left, top, width: w, height: h,
          border: '2px solid #6366f1',
          background: 'rgba(99,102,241,0.10)',
          borderRadius: 6,
          zIndex: 9,
          pointerEvents: 'none',
        }}
      />
      {/* iter69 · Sidebar mode: render a small circular trigger button
          in the right margin, Y-aligned with the halo. Clicking it
          opens/closes the sidebar recorder panel. Legacy in-page mode:
          render the accordion panel next to the halo as before. */}
      {useSidebar ? (
        <button
          type="button"
          data-testid={`audio-exercise-trigger-${exercise_id}`}
          onClick={(e) => { e.stopPropagation(); toggleOpen(); }}
          title={isOpen ? 'Cerrar grabador' : `Abrir grabador ${exercise_id}`}
          style={{
            // iter71.6 · Estético: junto al icono del micrófono aparece
            // el texto "Grabar" en blanco. El botón crece horizontal-
            // mente y cambia a pill para no cortar el texto. Cero
            // cambios en la lógica interna del grabador ni en el
            // flujo de guardado (`toggleOpen`, `overlayHasAnyPersisted`,
            // `isPreSubmit`… quedan intactos).
            position: 'absolute',
            right: 4,
            top,
            height: 34,
            padding: '0 10px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 6,
            background: isOpen ? '#4338ca' : '#dc2626',
            color: '#fff',
            border: '2px solid #fff',
            borderRadius: 999,
            boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
            cursor: 'pointer',
            zIndex: 11,
            whiteSpace: 'nowrap',
            fontSize: 13,
            fontWeight: 600,
            lineHeight: 1,
          }}
        >
          <Mic size={16} />
          <span data-testid={`audio-exercise-trigger-label-${exercise_id}`}>
            {isOpen ? 'Cerrar' : 'Grabar'}
          </span>
          {(overlayHasAnyPersisted || (isPreSubmit && exercise._preSubmitLocal)) && !isOpen && (
            <span
              data-testid={`audio-trigger-badge-${exercise_id}`}
              style={{
                position: 'absolute', top: -4, right: -4,
                fontSize: 9, background: '#eef2ff', color: '#4338ca',
                padding: '1px 4px', borderRadius: 8, fontWeight: 700,
                border: '1px solid #c7d2fe', minWidth: 14, lineHeight: 1.2,
              }}
              title="Hay audios grabados"
            >
              {(myPersistedAudioId ? 1 : 0) + (otherPersistedAudioId ? 1 : 0) + (isPreSubmit && exercise._preSubmitLocal ? 1 : 0)}
            </span>
          )}
        </button>
      ) : panelBody}
      {/* When in sidebar mode AND open, portal the full panel to the
          viewer-provided host so it stays put during PDF scroll. */}
      {useSidebar && isOpen && sidebarHost && createPortal(panelBody, sidebarHost)}
    </>
  );
}
